import { useMemo } from 'react';
import { Button } from '@lingoda/ui';
import { ChecklistItemSlug } from '@lingoda/monolith-graphql';
import {
    classroomUrlRedirectPath,
    resolvePath,
    studentBookingPath,
    studentClassDetailsPath,
} from '@lingoda/router';
import { trans } from '@lingoda/i18n';
import { useStudentAppointment } from '../../../../hooks/useStudentAppointment';
import { checklistActionsTranslations } from '../translations';

type ActionButtonProps = {
    slug: ChecklistItemSlug;
};

export const ActionButton = ({ slug }: ActionButtonProps) => {
    const { previousAppointment, nextAppointment, isInSession, isNextClassToday, timeRemaining } =
        useStudentAppointment();

    const nextClassUid = nextAppointment?.class?.uid;
    const prevClassUid = previousAppointment?.class?.uid;

    const text = useMemo(() => {
        if (slug === ChecklistItemSlug.AttendClass) {
            if (isNextClassToday && timeRemaining) {
                return trans('join-in', { timeRemaining }, 'student-today');
            }
            if (isInSession) {
                return trans('join-class-now', {}, 'student-today');
            }
        }

        return checklistActionsTranslations[slug]?.cta;
    }, [slug, isNextClassToday, timeRemaining, isInSession]);

    const buttonProps = useMemo(() => {
        switch (slug) {
            case ChecklistItemSlug.AttendClass:
                return {
                    href: nextClassUid && classroomUrlRedirectPath({ classId: nextClassUid }),
                    target: '_blank',
                    disabled: !isInSession,
                };
            case ChecklistItemSlug.ReviewAfterClass:
            case ChecklistItemSlug.Practice:
                return {
                    href: prevClassUid
                        ? resolvePath(studentClassDetailsPath({ class_id: prevClassUid }))
                        : undefined,
                };
            case ChecklistItemSlug.BookFirstClass:
                return {
                    href: resolvePath(studentBookingPath()),
                };
            case ChecklistItemSlug.WarmupBeforeClass:
                return {
                    href: nextClassUid
                        ? resolvePath(studentClassDetailsPath({ class_id: nextClassUid }))
                        : undefined,
                };
            default:
                return {};
        }
    }, [slug, nextClassUid, prevClassUid, isInSession]);

    return (
        <Button variant="contained" color="primary" sx={{ flexShrink: '0' }} {...buttonProps}>
            {text}
        </Button>
    );
};
